'use client';

/* eslint-disable max-lines-per-function */
import React, {
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { type TMenu } from '@/common/types/analytics.type';
import { type Status } from '@/common/types/auth.type';
import {
  selectDomain,
  selectIsTenant,
  useDomainStore,
} from '@/store/use-domain.store';
import { Dropdown, Skeleton } from 'antd';

import { useGetProfile } from '@/common/apis/profile/v1/get-profile.api';
import AnalyticsWrapper from '@/common/components/analytics-wrapper';
import Button from '@/common/components/button';
import CardDivider from '@/common/components/card-divider';
import CustomerSupportDrawer from '@/common/components/customer-support-drawer';
import List from '@/common/components/list';
import { PopUpModal } from '@/common/components/modal/popupModal.component';
import Text from '@/common/components/text';
import { appConfigs } from '@/common/configs/app.config';
import { VendorShareContext } from '@/common/contexts/vendor-share.context';
import { manualLogout } from '@/common/utils/auth-actions.client';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';

import BasketIcon from '~/public/assets/images/svg/basket.svg';
import ExitIcon from '~/public/assets/images/svg/exit.svg';
import HeadphoneIcon from '~/public/assets/images/svg/headphone.svg';
import ProfileIcon from '~/public/assets/images/svg/profileIcon.svg';
import ShareIcon from '~/public/assets/images/svg/share.svg';
import UserIcon from '~/public/assets/images/svg/user.svg';
import VendorRegisteration from '~/public/assets/images/svg/vendor-register-icon.svg';
import WalletIcon from '~/public/assets/images/svg/wallet.svg';

import { type SettingItem } from './setting.component';

export interface SettingDropDownProps {
  className?: string;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  iconOnly?: boolean;
}

export default function SettingDropDown({
  className,
  status,
  iconOnly = false,
}: SettingDropDownProps): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);

  const [openExit, setOpenExit] = useState(false);
  const { vendorName, vendorSharable, vendorSlug } =
    useContext(VendorShareContext);
  const pathname = usePathname();

  const isSharable = pathname.slice(1) === vendorSlug;
  const isTenant = useDomainStore(selectIsTenant);
  const config = useDomainStore(selectDomain);
  const { data: userProfile } = useGetProfile({
    enabled: status === 'authenticated',
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleSupportDialogToggle = () => {
    setOpenSupport((prev) => !prev);
  };
  const onShareClickHandler = async () => {
    if (navigator && 'share' in navigator) {
      navigator.share({
        title: vendorName,
        text: ` این سایت فروشگاه ${vendorName} هستش. یه نگاه بکن محصولات خوبی داره.`,
        url: window.location.href,
      });
    }
  };

  const onCloseExitDrawerHandler = () => {
    setOpenExit(false);
  };

  const onExitClickHandler = () => {
    setOpenExit(true);
    setOpen(false);
  };

  const onExit = () => {
    setOpenExit(false);
    void manualLogout('/');
  };

  const items: SettingItem[] = [
    {
      icon: <BasketIcon className="h-5 w-5 text-secondary-300" />,
      link: `/orders${createQueryStringUtility({ from: vendorSlug })}`,
      role: ['authenticated'],
      title: 'سفارشات',
      id: 'menu-click-order-v1',
    },
    {
      icon: <WalletIcon className="h-5 w-5 text-secondary-300" />,
      link: `/wallet${createQueryStringUtility({ from: vendorSlug })}`,
      role: ['authenticated'],
      title: 'کیف پول',
      id: 'menu-click-wallet-v1',
    },

    ...(vendorSharable && isSharable
      ? [
          {
            icon: <ShareIcon className="h-5 w-5 text-secondary-300" />,
            title: 'اشتراک گذاری فروشگاه',
            role: ['authenticated', 'unauthenticated'] as Status[],
            onClick: onShareClickHandler,
            id: 'menu-click-share-store-v1' as keyof TMenu,
          },
        ]
      : []),

    {
      icon: <HeadphoneIcon className="h-5 w-5 text-secondary-300" />,
      title: 'پشتیبانی',
      role: ['authenticated', 'unauthenticated'],
      onClick: () => {
        setOpenSupport(true);
      },
      id: 'menu-click-support-v1',
    },

    {
      icon: <VendorRegisteration className="h-5 w-5 text-secondary-300" />,
      title: 'ورود / ثبت نام فروشندگان',
      role: ['authenticated', 'unauthenticated'],
      link: `${appConfigs.witroAppUrl}`,
      id: 'menu-click-vendor-register-v1',
    },

    {
      icon: <ExitIcon className="h-5 w-5 text-secondary-300" />,
      title: 'خروج',
      role: ['authenticated'] as Status[],
      onClick: onExitClickHandler,
      id: 'menu-submit-logout-v1',
    },
  ];

  // allowed item ids when the user is tenant (only these should be visible)
  const tenantAllowedIds = [
    'menu-click-order-v1',
    'menu-click-support-v1',
    'menu-submit-logout-v1',
  ];

  const hamburgerItems = useMemo(() => {
    const candidateItems = isTenant
      ? items.filter((item) => !!item.id && tenantAllowedIds.includes(item.id))
      : items;

    return candidateItems.filter((item) => item.role?.includes(status));
  }, [status, vendorName, isTenant]);

  return (
    <>
      <Dropdown
        dropdownRender={(originNode) => {
          return (
            <List className="shadow-[-2px_7px_14.2px_0px_rgba(23, 23, 23, 0.07)] min-w-[360px] rounded-2xl border border-[#F2F1F1] !bg-white px-4">
              {status === 'authenticated' && (
                <List.Item hiddenIcon>
                  <List.Icon className="bg-silver-200">
                    <UserIcon className="h-5 w-5 text-secondary-300" />
                  </List.Icon>
                  <List.Property className="w-full justify-between">
                    <List.Property>{userProfile?.fullName}</List.Property>
                    <List.Property>
                      <AnalyticsWrapper analytics={['menu-click-profile-v1']}>
                        <Button
                          id="menu-click-profile-v1"
                          type="outline"
                          className="h-[32px]"
                          link="/profile"
                        >
                          ویرایش پروفایل
                        </Button>
                      </AnalyticsWrapper>
                    </List.Property>
                  </List.Property>
                </List.Item>
              )}
              <CardDivider itemClassName="!p-0" />
              {hamburgerItems.map((item, index) =>
                item.id ? (
                  <AnalyticsWrapper analytics={[item.id]} key={index}>
                    <Link
                      id={item.id}
                      href={item.link ?? ''}
                      onClick={item.onClick}
                    >
                      <List.Item>
                        <List.Icon className="bg-silver-200">
                          {item.icon}
                        </List.Icon>
                        <List.Property>{item.title}</List.Property>
                      </List.Item>
                    </Link>
                  </AnalyticsWrapper>
                ) : (
                  <Link
                    key={index}
                    id={item.id}
                    href={item.link ?? ''}
                    onClick={item.onClick}
                  >
                    <List.Item>
                      <List.Icon className="bg-silver-200">
                        {item.icon}
                      </List.Icon>
                      <List.Property>{item.title}</List.Property>
                    </List.Item>
                  </Link>
                )
              )}
            </List>
          );
        }}
        open={open}
      >
        <div
          className={
            iconOnly
              ? 'relative h-12 w-11 shrink-0'
              : 'mr-[20px] h-full w-full lg:!h-full'
          }
          ref={ref}
        >
          <div
            className={
              iconOnly ? 'absolute inset-x-0 bottom-0 flex h-11 w-11' : 'h-full'
            }
          >
            {iconOnly ? (
              <button
                type="button"
                aria-label="حساب کاربری"
                onClick={() => {
                  setOpen(!open);
                }}
                className={`
                  flex h-11 w-11 items-center justify-center
                  rounded-[10px] bg-[#EDECEB] p-0 leading-none
                  ${className ?? ''}
                `}
              >
                <ProfileIcon className="block h-[19px] w-[14px] shrink-0 text-[#191308]" />
              </button>
            ) : (
              <Button
                type="tonal"
                onClick={() => {
                  setOpen(!open);
                }}
                className={`flex h-full w-full flex-row items-center justify-center gap-2 !border-none !text-[#191308] lg:!h-full lg:!rounded-[10px] lg:!bg-[#F6F6F5] ${className ?? ''}`}
              >
                <ProfileIcon className="h-6 w-6 text-black-default" />
                {status === 'loading' ? (
                  <Skeleton
                    active
                    paragraph={{ rows: 1, width: '65px' }}
                    title={false}
                  />
                ) : (
                  userProfile?.phoneNumber
                )}
              </Button>
            )}
          </div>
        </div>
      </Dropdown>
      <PopUpModal
        onCancel={onCloseExitDrawerHandler}
        open={openExit}
        width={504}
        centered
      >
        <PopUpModal.Body className="px-5 py-6">
          <div className="flex flex-col gap-4">
            <Text className="text-xl font-semibold">خروج از حساب کاربری</Text>
            <Text className="text-base font-medium">
              در صورت خروج از حساب کاربری‌تون، محتوای سبد خریدتون بدون تغییر
              باقی می‌مونه.
            </Text>
          </div>
        </PopUpModal.Body>
        <PopUpModal.Footer>
          <div className="flex w-full items-center gap-3">
            <Button type="outline" className="w-full" onClick={onExit}>
              خروج
            </Button>
            <Button
              type="black"
              className="w-full"
              onClick={onCloseExitDrawerHandler}
            >
              بازگشت
            </Button>
          </div>
        </PopUpModal.Footer>
      </PopUpModal>
      <CustomerSupportDrawer
        title="تماس با پشتیبانی"
        description={`برای ارتباط با تیم پشتیبانی ${isTenant ? config?.title : 'ویترو'} می‌تونین از روش‌های زیر اقدام کنین:`}
        openSupport={openSupport}
        handleToggle={handleSupportDialogToggle}
      />
    </>
  );
}
